import React, {
  FC,
  ComponentProps,
  useEffect,
  useCallback,
  useState,
  SyntheticEvent,
} from 'react'
import get from 'lodash/get'

import isEmpty from 'lodash/isEmpty'
import * as CustomEvent from '@/checkout-instore-legacy/utils/CustomEvent'
import * as CatalogUtils from '@/checkout-instore-legacy/utils/CatalogUtils'
import { endCatalogStepEvent } from '@/checkout-instore-legacy/utils/EventHelper'
import { refreshSession } from '@/checkout-instore-legacy/utils/sessionUtils'
import {
  getOrderFormId,
  getOrderForm,
} from '@/checkout-instore-legacy/utils/orderFormUtils'
import * as EmailUtils from '@/checkout-instore-legacy/utils/EmailUtils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import BarcodeReader from '@/checkout-instore-legacy/utils/BarcodeReader'
import SearchContent from './SearchContent'
import { useDebounced } from '@/checkout-instore-legacy/hooks/useDebounced'
import { memoizedDebounce } from '@/checkout-instore-legacy/utils/debounceUtils'
import Toolbar from '@/checkout-instore-legacy/cart/components/Toolbar'
import Attachments from '@/checkout-instore-legacy/components/Attachments'
import { AttachmentsProvider } from '@/checkout-instore-legacy/components/Attachments/provider/AttachmentsProvider'
import { checkShouldRedirectToProductPage } from './utils/checkShouldRedirectToProductPage'

const EAN_MIN_LENGTH = 8
const DEBOUNCE_DEFAULT_TIME = 800

interface SearchArgs {
  term: string
  lock: boolean
}

interface QueryRouter {
  term: string | null
  skipDebounce?: boolean
  skuId?: string
}

interface StateRouter {
  origin: string
  isLuckyResult: boolean
}

type SearchPageProps = {
  clearSearch: () => void
  clearLoadings: () => void
  clearUpdateFilterAttributes: () => void
  updateCartAddressWithSimulationAddress: () => void
  router: RouterProps<QueryRouter, StateRouter>
  route: Route<QueryRouter, StateRouter>
  clientProfileData: {
    email: string
  }
  setCustomer: ({ identification }: { identification: string }) => void
  location: {
    query: {
      term: string
      skipDebounce: string
    }
  }
  logisticsInfoCache: LogisticsInfo
  addRecentTerm: ({
    term,
    productName,
  }: {
    term: string
    productName: string
  }) => void
  beginSearch: (term: string) => void
  addResults: (results: Product[]) => void
  fetchNextSearch: (term: string, page: number) => void
  nextSearch: (term: string, page: number) => void
  page: number
} & ComponentProps<typeof SearchContent>

// @ts-expect-error ts-migrate(6196) FIXME: 'FiltersSearch' is declared but never used.
type FiltersSearch = ComponentProps<typeof SearchContent>['filters']

const CLEAR_FILTERS_DEBOUNCE_TIME = 300

const SearchPageComponent: FC<SearchPageProps> = ({
  clearSearch,
  clearLoadings,
  clearUpdateFilterAttributes,
  router,
  clientProfileData,
  setCustomer,
  results,
  logisticsInfoCache,
  term,
  location,
  addRecentTerm,
  beginSearch,
  loading,
  recentTerms,
  addResults,
  fetchNextSearch,
  nextSearch,
  intervalBegin,
  nextResults,
  nextLoading,
  loadingProducts,
  errors,
  page,
  abortSearch,
  filters,
  submitFilter,
  reloadFilters,
  updateCartAddressWithSimulationAddress,
}) => {
  const { query } = location
  const { enableAttachments } = getInstoreConfig()
  const clearSearchCb = useCallback(clearSearch, [])
  const clearLoadingsCb = useCallback(clearLoadings, [])
  const clearUpdateFilterAttributesCb = useCallback(
    memoizedDebounce(clearUpdateFilterAttributes, CLEAR_FILTERS_DEBOUNCE_TIME),
    []
  )
  const onSearch = (search: { term: string; lock?: boolean }) => {
    const { term, lock } = search
    const containsSpace = /\s/.test(term)
    if (lock && !containsSpace && term?.length >= EAN_MIN_LENGTH) {
      beginSearch(term)
    } else {
      setLock(false)
    }
  }
  const [lock, setLock] = useState(true) // lock debounce search
  const { handleSearchChange, handleSearchClear } = useDebounced<SearchArgs>({
    value: { term, lock },
    onChange: onSearch,
    debounceTime: DEBOUNCE_DEFAULT_TIME,
  })

  const searchTermWithDebounce = (termSearched: string) => {
    if (termSearched !== term) {
      if (!loading) {
        clearUpdateFilterAttributesCb()
        handleSearchChange({ term: termSearched, lock })
      }
    } else {
      handleSearchClear()
      clearSearchCb()
    }
  }

  useEffect(() => {
    return () => {
      updateCartAddressWithSimulationAddress()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    function whenQueryUpdates() {
      if (query.skipDebounce) {
        handleSearchClear()

        if (query.term !== term) {
          beginSearch(query.term)
        }
      } else {
        searchTermWithDebounce(query.term)
      }
    },
    // this effect must run only if query changes in order to keep consistency
    // between the the info in the URL and the current results
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query]
  )

  useEffect(
    function cleanSearchWithoutTerm() {
      if (isEmpty(query.term)) {
        setLock(true)
        clearSearchCb()
      }
    },
    [query, clearSearchCb]
  )

  useEffect(function whenMountPage() {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(event: SyntheticEvent) => any' ... Remove this comment to see the full error message
    CustomEvent.listenEvent('topbar.back', handleBack)
    initClient()
    initBarcodeReader()
    return () => {
      clearSearchCb()
      clearLoadingsCb()
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(event: SyntheticEvent) => any' ... Remove this comment to see the full error message
      CustomEvent.unlistenEvent('topbar.back', handleBack)
      finishBarcodedReader()
    }
    // This effect must run only when the page is mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(
    function whenResultsAreUpdated() {
      if (!results.length) {
        return
      }

      const shouldRedictToProductPage = checkShouldRedirectToProductPage(
        results,
        term
      )

      if (shouldRedictToProductPage) {
        const [firstResult] = results

        const hasPriceVariation = CatalogUtils.hasPriceVariation(
          firstResult,
          logisticsInfoCache
        )

        const sku = hasPriceVariation
          ? CatalogUtils.getCheapestAvailableSku(
              firstResult,
              logisticsInfoCache
            )
          : CatalogUtils.getFirstSkuWithStock(firstResult, logisticsInfoCache)

        const hasMultipleSkus = CatalogUtils.hasMultipleSkus(firstResult)
        const skus = hasMultipleSkus ? null : get(sku, 'id')

        goToProductPage(get(firstResult, 'id'), skus, false, true)
      }
    },
    // avoided exaustive-deps rule here because there's no need to pass goToProduct and
    // shouldSkipProductPage as dependencies. I dont want that everytime that this function
    // runs trigger this effect, and the second is
    // derived from a property in instore config (that will no update too)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results, term, logisticsInfoCache]
  )

  const initBarcodeReader = () => {
    BarcodeReader.listen(handleBarcodeRead)
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type '{ barcode... Remove this comment to see the full error message
    window.handleBarcodeRead = (barcode) => handleBarcodeRead({ data: barcode }) // used on custom scripts
  }

  const finishBarcodedReader = () => {
    BarcodeReader.unlisten(handleBarcodeRead)
    window.handleBarcodeRead = null
  }

  const initClient = () => {
    if (clientProfileData) {
      const { email } = clientProfileData
      const orderForm = getOrderForm()
      refreshSession(getOrderFormId(orderForm), email)
    } else {
      const email = EmailUtils.getNewAnonymousEmail()
      setCustomer({ identification: email })
    }
  }

  const handleBarcodeRead = (event: { data: { barcode: string } }) => {
    const { barcode } = event.data
    if (barcode) {
      const rawBarcode = barcode.toString()
      updateTerm(rawBarcode, true) // update search term without debounce
    }
  }

  const goToProductPage = (
    productId: string,
    skuId: string,
    isLuckyResult?: boolean,
    skippedResults?: boolean
  ) => {
    const product = results.find((item) => get(item, 'id') === productId)
    const productName = get(product, 'name', '')
    const routerFunc = isLuckyResult ? router.replace : router.push
    const isTermAlternateId = CatalogUtils.isAlternateId(location.query.term)

    addRecentTerm({ term, productName })

    routerFunc({
      pathname: `/product/${productId}`,
      query: {
        skuId,
        term: isTermAlternateId ? null : term,
        ...(skippedResults ? { skippedResults: true } : null),
      },
      state: {
        origin: 'search',
        isLuckyResult: isTermAlternateId ? false : !!isLuckyResult,
      },
    })
  }

  const updateTerm = (term: string, skipDebounce?: boolean) => {
    router.replace({
      pathname: '/search',
      query: {
        term,
        skipDebounce,
      },
    })
  }

  const handleBack = (event: SyntheticEvent) => {
    event.preventDefault()
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'data' does not exist on type 'SyntheticE... Remove this comment to see the full error message
    const { router, location } = event.data
    const { query } = location
    if (!query.term || !query.term.length) {
      endCatalogStepEvent()
      return router.replace('/cart')
    }
    router.goBack()
  }

  const retrySearch = () => {
    beginSearch(query.term)
  }

  const moreItems = () => {
    addResults(nextResults)
    fetchNextSearch(term, page + 1)
    nextSearch(term, page + 1)
  }

  return (
    <AttachmentsProvider>
      <main className="flex-auto overflow-auto momentum bg-muted-5 relative">
        <div className="bt b--muted-4">
          <Toolbar buttonsEnabled={false} />
        </div>

        <SearchContent
          recentTerms={recentTerms}
          retrySearch={retrySearch}
          updateTerm={updateTerm}
          onClickSearchResult={goToProductPage}
          moreItems={moreItems}
          results={results}
          loadingProducts={loadingProducts}
          intervalBegin={intervalBegin}
          term={term}
          nextResults={nextResults}
          nextLoading={nextLoading}
          loading={loading}
          errors={errors}
          abortSearch={abortSearch}
          filters={filters}
          submitFilter={submitFilter}
          reloadFilters={reloadFilters}
        />
      </main>
      {enableAttachments && <Attachments />}
    </AttachmentsProvider>
  )
}

export const SearchPage = SearchPageComponent
